import styled, { css, keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import DesignTokens from '../styles/DesignTokens';
import G from '../styles/GlassmorphismTokens';

// ===== SCENIC BACKGROUND — same source as ItineraryPage =====

// ===== COLORS — aligned with GlassmorphismTokens / ItineraryPage =====
const C = {
  bg: '#000c1a',                              // fallback solid bg behind scenic image
  primary: '#4285F4',
  primaryHover: '#1A73E8',
  primaryLight: 'rgba(66, 133, 244, 0.15)',
  primaryRing: 'rgba(66, 133, 244, 0.35)',
  accent: '#60a5fa',
  // Text — white on glass (same as ItineraryPage FormContainer)
  text: '#ffffff',
  textSecondary: 'rgba(255, 255, 255, 0.75)',
  textMuted: 'rgba(255, 255, 255, 0.5)',
  white: '#ffffff',
  pink: '#f472b6',
  emerald: '#10b981',
  yellow: '#facc15',
  red: '#ef4444',
};

// ===== ANIMATIONS =====
const pulseRing = keyframes`
  0% { transform: scale(0.33); opacity: 1; }
  80%, 100% { transform: scale(1.5); opacity: 0; }
`;

const pulseDot = keyframes`
  0%, 100% { transform: scale(0.85); }
  50% { transform: scale(1); }
`;

// ===== PAGE LAYOUT =====
export const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  /* No padding-top — Navbar floats over the map/page as glass overlay.
     Sidebar compensates with its own padding-top. */
  padding-top: 0;
  box-sizing: border-box;
  /* In list view lock to viewport; in map view allow scroll so footer is reachable */
  ${props => props.$mapActive
    ? css`overflow: visible;`
    : css`height: 100vh; overflow: hidden;`
  }
  color: ${C.text};
  font-family: ${DesignTokens.typography.fontFamily.primary};
  background-color: ${C.bg};
  background-image: url(${G.scenicBackground});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    /* Darker overlay — more contrast, keeps glass elements readable */
    background: linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.72) 100%);
    pointer-events: none;
    z-index: 0;
  }

  > * { position: relative; z-index: 1; }
`;

// ===== DESKTOP HEADER =====
export const DesktopHeader = styled.header`
  display: none;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem;
  border-bottom: ${G.border.medium};
  background: ${G.glass.medium};
  backdrop-filter: ${G.blur.lg};
  -webkit-backdrop-filter: ${G.blur.lg};
  z-index: ${DesignTokens.zIndex.sticky};

  @media (min-width: ${props => props.theme.breakpoints.desktop}) {
    display: flex;
  }
`;

export const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const LogoIcon = styled.div`
  width: 32px;
  height: 32px;
  background: ${G.gradient.blueCTA};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${C.white};
  font-size: 1.25rem;
`;

export const LogoText = styled.h1`
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.01em;

  span {
    color: ${C.primary};
    font-weight: 900;
    font-style: italic;
  }
`;

export const SearchBarWrapper = styled.div`
  flex: 1;
  max-width: 560px;
  padding: 0 3rem;
`;

export const SearchBar = styled.div`
  position: relative;

  svg {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    width: 18px;
    height: 18px;
    color: ${C.textMuted};
    transition: color 0.2s;
  }

  &:focus-within svg {
    color: ${C.primary};
  }
`;

export const SearchInput = styled.input`
  width: 100%;
  background: ${G.glass.medium};
  backdrop-filter: ${G.blur.sm};
  -webkit-backdrop-filter: ${G.blur.sm};
  border: ${G.border.light};
  border-radius: 9999px;
  padding: 0.5rem 1rem 0.5rem 2.5rem;
  font-size: 0.875rem;
  color: ${C.text};
  outline: none;
  transition: box-shadow 0.2s, border-color 0.2s;

  &::placeholder {
    color: ${C.textMuted};
  }

  &:focus {
    border-color: rgba(66, 133, 244, 0.5);
    box-shadow: 0 0 0 3px ${C.primaryRing};
  }
`;

export const HeaderNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
`;

export const HeaderNavLink = styled.a`
  color: ${C.textSecondary};
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: ${C.text};
  }
`;

export const CreateButton = styled.a`
  padding: 0.5rem 1rem;
  background: ${G.gradient.blueCTA};
  color: ${C.white};
  border-radius: 9999px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: box-shadow 0.2s, opacity 0.2s;
  box-shadow: ${G.shadow.blueCTA};

  &:hover {
    opacity: 0.9;
    box-shadow: ${G.shadow.blueCTAHover};
  }
`;

export const HeaderDivider = styled.div`
  width: 1px;
  height: 20px;
  background: ${G.border.light};
`;

export const UserAvatarImg = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(66, 133, 244, 0.3);
`;

export const LogoImg = styled.img`
  height: 36px;
  width: auto;
  display: block;
`;

export const DarkUserMenuWrapper = styled.div`
  > button {
    border-color: rgba(255, 255, 255, 0.18) !important;

    &:hover {
      background: rgba(255, 255, 255, 0.08) !important;
      border-color: rgba(255, 255, 255, 0.28) !important;
    }

    span {
      color: ${C.text} !important;
    }
  }
`;

// ===== MOBILE HEADER =====
export const MobileHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: ${G.border.medium};
  background: ${G.glass.medium};
  backdrop-filter: ${G.blur.lg};
  -webkit-backdrop-filter: ${G.blur.lg};
  z-index: ${DesignTokens.zIndex.sticky};

  @media (min-width: ${props => props.theme.breakpoints.desktop}) {
    display: none;
  }
`;

export const MobileSearchButton = styled.button`
  padding: 0.5rem;
  border-radius: 50%;
  background: none;
  border: none;
  color: ${C.text};
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

// ===== MAIN LAYOUT =====
export const ExplorerLayout = styled.main`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
`;

// ===== SIDEBAR =====
export const Sidebar = styled.aside`
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  /* Dark glass panel — always readable over scenic background */
  background: rgba(8, 14, 32, 0.80);
  backdrop-filter: ${G.blur.lg};
  -webkit-backdrop-filter: ${G.blur.lg};
  @supports not (backdrop-filter: blur(1px)) {
    background: rgba(8, 14, 32, 0.95);
  }
  position: relative;
  z-index: 40;
  /* Push content below the floating glass Navbar (~64px) */
  padding-top: 12px;

  @media (min-width: ${props => props.theme.breakpoints.desktop}) {
    width: 340px;
    flex-shrink: 0;
    border-right: ${G.border.medium};
    display: flex !important;
    padding-top: 72px;
  }
`;

export const SidebarHeader = styled.div`
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: ${G.border.light};

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    padding: 0.75rem 1rem 0.5rem;
  }
`;

export const PageTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: ${C.text};

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    font-size: 1.25rem;
    margin-bottom: 0.75rem;
  }
`;

// ===== SURPRISE ME BUTTON =====
export const SurpriseButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  border-radius: 9999px;
  border: 1px solid rgba(66, 133, 244, 0.35);
  background: rgba(66, 133, 244, 0.12);
  color: ${C.accent};
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 0.75rem;
  white-space: nowrap;

  &:hover {
    background: rgba(66, 133, 244, 0.22);
    border-color: rgba(66, 133, 244, 0.5);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(66, 133, 244, 0.2);
  }

  &:active {
    transform: scale(0.97);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// ===== VIEW TOGGLE (mobile) =====
export const ViewToggleWrapper = styled.div`
  display: flex;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  padding: 3px;
  border-radius: 10px;
  margin-bottom: 0.75rem;

  @media (min-width: ${props => props.theme.breakpoints.desktop}) {
    display: none;
  }
`;

export const ViewToggleButton = styled.button`
  flex: 1;
  padding: 0.4rem 0.75rem;
  font-size: 0.8125rem;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.$active
    ? css`
      background: rgba(255, 255, 255, 0.15);
      color: ${C.white};
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
    `
    : css`
      background: transparent;
      color: ${C.textMuted};

      &:hover {
        color: ${C.text};
        background: rgba(255, 255, 255, 0.05);
      }
    `}
`;

// ===== FILTER CHIPS =====
export const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  align-items: center;

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    flex-wrap: wrap;
    padding-bottom: 0.25rem;
  }
`;

export const FilterChip = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.75rem;
  background: ${props => props.$active
    ? 'rgba(66, 133, 244, 0.2)'
    : 'rgba(255, 255, 255, 0.10)'};
  border: ${props => props.$active
    ? '1px solid rgba(66, 133, 244, 0.45)'
    : '1px solid rgba(255, 255, 255, 0.18)'};
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${props => props.$active ? C.accent : C.text};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$active
      ? 'rgba(66, 133, 244, 0.28)'
      : 'rgba(255, 255, 255, 0.16)'};
    border-color: ${props => props.$active
      ? 'rgba(66, 133, 244, 0.6)'
      : 'rgba(255,255,255,0.35)'};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const AISortChip = styled(FilterChip)`
  margin-left: auto;
  background: rgba(66, 133, 244, 0.15);
  color: ${C.accent};
  border-color: rgba(66, 133, 244, 0.3);
  font-weight: 700;

  &:hover {
    background: rgba(66, 133, 244, 0.25);
  }

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    margin-left: 0;
  }
`;

// ===== CARD LIST (scrollable) =====
export const CardList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 1.5rem 3rem;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 10px;
  }

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    padding: 0 1rem 4.5rem;
  }
`;

export const CardListInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

// ===== ITINERARY CARD — glassmorphic =====
export const ItinCard = styled(motion.div)`
  position: relative;
  background: ${props => props.$selected
    ? 'rgba(66, 133, 244, 0.22)'
    : 'rgba(255, 255, 255, 0.07)'};
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
  border: ${props => props.$selected
    ? `1.5px solid rgba(66, 133, 244, 0.55)`
    : '1px solid rgba(255, 255, 255, 0.14)'};
  box-shadow: ${props => props.$selected
    ? `0 0 0 1px ${G.dark.border}, ${G.shadow.glassLg}`
    : G.shadow.glass};

  &:hover {
    background: ${props => props.$selected
      ? 'rgba(66, 133, 244, 0.28)'
      : 'rgba(255, 255, 255, 0.12)'};
    border-color: ${props => props.$selected
      ? 'rgba(66, 133, 244, 0.65)'
      : 'rgba(255, 255, 255, 0.28)'};
    box-shadow: ${G.shadow.glassLg};
  }
`;

// Desktop compact layout (thumbnail left, text right)
export const DesktopCardInner = styled.div`
  display: none;
  padding: 0.875rem;
  gap: 0.875rem;

  @media (min-width: ${props => props.theme.breakpoints.desktop}) {
    display: flex;
  }
`;

export const CardThumbnail = styled.div`
  width: 88px;
  height: 88px;
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.4), transparent);
  }
`;

export const CardBody = styled.div`
  flex: 1;
  min-width: 0;
`;

export const CardTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.2rem;
`;

export const CardTitle = styled.h3`
  font-weight: 700;
  font-size: 0.875rem;
  line-height: 1.3;
  padding-right: 1rem;
  color: ${C.text};
`;

export const FavoriteIcon = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${props => props.$active ? C.pink : C.textMuted};
  font-size: 1.125rem;
  transition: color 0.2s, transform 0.2s;
  flex-shrink: 0;

  &:hover {
    color: ${C.pink};
    transform: scale(1.15);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const CardMeta = styled.div`
  font-size: 0.6875rem;
  color: ${C.textSecondary};
  line-height: 1.6;

  strong {
    color: ${C.textSecondary};
    font-weight: 600;
  }

  .label {
    color: ${C.text};
    font-weight: 600;
  }
`;

export const CardHighlights = styled.p`
  font-size: 0.6875rem;
  color: ${C.textSecondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  .label {
    color: ${C.text};
    font-weight: 600;
  }
`;

export const CardUser = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.6875rem;
  color: ${C.textSecondary};

  img {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    object-fit: cover;
  }
`;

// ===== HOVER OVERLAY (desktop) =====
export const HoverOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(8, 14, 32, 0.72);
  backdrop-filter: ${G.blur.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 10;

  ${ItinCard}:hover & {
    opacity: 1;
  }

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    display: none;
  }
`;

export const OverlayAction = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  background: none;
  border: none;
  cursor: pointer;
  color: ${C.white};
`;

export const OverlayActionCircle = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  background: ${props => props.$primary ? C.primary : 'rgba(255,255,255,0.15)'};
  border: 1px solid ${props => props.$primary ? 'transparent' : 'rgba(255,255,255,0.3)'};
  backdrop-filter: blur(8px);
  color: ${C.white};

  &:hover {
    background: ${C.primary};
    transform: scale(1.1);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const OverlayActionLabel = styled.span`
  font-size: 0.625rem;
  font-weight: 500;
`;

// ===== MOBILE CARD (tall hero image, full width) =====
export const MobileCardInner = styled.div`
  display: block;

  @media (min-width: ${props => props.theme.breakpoints.desktop}) {
    display: none;
  }
`;

export const MobileCardImage = styled.div`
  height: 140px;
  overflow: hidden;
  position: relative;
  border-radius: 12px 12px 0 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const MobileCardFavorite = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  padding: 0.5rem;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(8px);
  border-radius: 50%;
  border: none;
  cursor: pointer;
  color: ${props => props.$active ? C.pink : C.white};
  z-index: 5;
  transition: color 0.2s;

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const MobileCardContent = styled.div`
  padding: 0.75rem 1rem;
`;

export const MobileCardTitle = styled.h3`
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 0.25rem;
  line-height: 1.3;
  color: ${C.text};
`;

export const MobileCardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.75rem;
  color: ${C.textSecondary};
  margin-bottom: 0.75rem;

  span {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const MobileCardHighlights = styled.p`
  font-size: 0.75rem;
  color: ${C.textSecondary};
  margin-bottom: 0.75rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
`;

export const StyleBadge = styled.span`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 6px;
  font-size: 0.6875rem;
  font-weight: 600;
  background: rgba(66, 133, 244, 0.15);
  color: #60a5fa;
  border: 1px solid rgba(66, 133, 244, 0.25);
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
`;

export const ExploreLink = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: #60a5fa;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    color: #93c5fd;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const MobileCardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const MobileCardUser = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${C.text};

  img {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    object-fit: cover;
  }
`;

export const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;

  svg {
    width: 14px;
    height: 14px;
  }

  ${props => props.$saved
    ? css`
      background: ${G.gradient.blueCTA};
      color: ${C.white};
      border: none;
      box-shadow: ${G.shadow.blueCTA};
    `
    : css`
      background: rgba(255, 255, 255, 0.10);
      color: ${C.text};
      border: 1px solid rgba(255, 255, 255, 0.18);
    `}

  &:active {
    transform: scale(0.95);
  }
`;

// ===== MAP PANEL =====
// On mobile we keep the map ALWAYS fixed at (0,0) with full viewport dimensions
// so Google Maps is never re-positioned and never needs a resize to fix its offset.
// We toggle visibility via z-index / pointer-events only.
export const MapPanel = styled.section`
  background: #0f172a;
  overflow: visible;
  position: relative;

  /* Mobile: full viewport height in normal flow (not fixed).
     Hidden when list view is active, shown when map view is active. */
  display: ${props => props.$mobileVisible ? 'flex' : 'none'};
  flex-direction: column;
  width: 100%;
  height: 100vh;

  > div {
    flex: 1;
    min-height: 0;
  }

  /* Desktop: always visible as flex child beside sidebar */
  @media (min-width: ${props => props.theme.breakpoints.desktop}) {
    display: flex;
    flex: 1;
    width: auto;
    height: 100%;
    min-height: 0;
  }
`;

/* kept for potential future use but not rendered */
export const MapBgImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.12;
  pointer-events: none;
`;

export const MapDotGrid = styled.div`
  position: absolute;
  inset: 0;
  background-image: radial-gradient(circle at 1.5px 1.5px, rgba(255, 255, 255, 0.09) 1px, transparent 0);
  background-size: 24px 24px;
  pointer-events: none;
`;

// ===== MAP PINS =====
export const MapPinContainer = styled.div`
  position: absolute;
  left: ${props => props.$left};
  top: ${props => props.$top};
  transform: translate(-50%, -50%);
  cursor: pointer;
  z-index: ${props => props.$active ? 20 : 10};

  &:hover {
    z-index: 25;
  }
`;

// Active itinerary pin with pulsing effect
export const ActivePin = styled.div`
  position: relative;
  width: 14px;
  height: 14px;
  background: ${C.white};
  border-radius: 50%;
  box-shadow: 0 0 0 3px rgba(255,255,255,0.35), 0 0 12px rgba(255,255,255,0.5);
  animation: ${pulseDot} 2s ease-in-out infinite;

  &::before {
    content: '';
    position: absolute;
    left: -160%;
    top: -160%;
    width: 420%;
    height: 420%;
    background-color: rgba(255, 255, 255, 0.25);
    border-radius: 50%;
    animation: ${pulseRing} 1.8s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
  }
`;

// Inactive small dot pin
export const InactivePin = styled.div`
  width: 10px;
  height: 10px;
  background: rgba(255, 255, 255, 0.65);
  border-radius: 50%;
  box-shadow: 0 0 0 3px rgba(255,255,255,0.15), 0 0 8px rgba(255,255,255,0.2);
  transition: all 0.2s;

  &:hover {
    background: ${C.white};
    box-shadow: 0 0 0 4px rgba(255,255,255,0.25), 0 0 14px rgba(255,255,255,0.4);
    transform: scale(1.3);
  }
`;

// Region cluster bubble
export const ClusterBubble = styled.div`
  width: ${props => props.$large ? '52px' : '42px'};
  height: ${props => props.$large ? '52px' : '42px'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$highlighted
    ? C.primary
    : 'rgba(255, 255, 255, 0.88)'};
  backdrop-filter: blur(8px);
  border: 2px solid ${props => props.$highlighted
    ? 'rgba(66, 133, 244, 0.5)'
    : 'rgba(255, 255, 255, 0.4)'};
  color: ${props => props.$highlighted ? C.white : '#0f172a'};
  font-size: ${props => props.$large ? '0.8125rem' : '0.6875rem'};
  font-weight: 800;
  box-shadow: 0 2px 12px rgba(0,0,0,0.4);

  &:hover {
    background: ${C.primary};
    color: ${C.white};
    border-color: ${C.primary};
    transform: scale(1.08);
    box-shadow: 0 4px 16px rgba(66,133,244,0.4);
  }
`;

export const ClusterNotification = styled.div`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 12px;
  height: 12px;
  background: ${C.red};
  border-radius: 50%;
  border: 2px solid #0a0f1e;
`;

// Pin label tooltip
export const PinLabel = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255,255,255,0.95);
  backdrop-filter: blur(8px);
  color: #0f172a;
  padding: 0.25rem 0.75rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%) translateY(-50%) rotate(45deg);
    width: 8px;
    height: 8px;
    background: rgba(255,255,255,0.95);
  }
`;

// Pin popover (hover detail card)
export const PinPopover = styled.div`
  position: absolute;
  ${props => props.$above !== false ? 'bottom: calc(100% + 16px);' : 'top: calc(100% + 16px);'}
  left: 50%;
  transform: translateX(-50%) ${props => props.$visible ? 'translateY(0)' : 'translateY(10px)'};
  width: 192px;
  background: rgba(12, 20, 42, 0.96);
  backdrop-filter: ${G.blur.lg};
  -webkit-backdrop-filter: ${G.blur.lg};
  border: 1px solid rgba(255, 255, 255, 0.16);
  padding: 0.75rem;
  border-radius: 16px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.65);
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
  z-index: 30;
`;

export const PopoverContent = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const PopoverImage = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
`;

export const PopoverInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const PopoverTitle = styled.p`
  font-size: 0.625rem;
  font-weight: 700;
  color: ${C.white};
  margin-bottom: 0.25rem;
  line-height: 1.3;
`;

export const PopoverMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.625rem;

  .cost {
    color: ${C.accent};
    font-weight: 700;
  }

  .days {
    color: ${C.textSecondary};
  }
`;

export const PopoverRating = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.25rem;
  color: ${C.yellow};
  font-size: 0.5625rem;
  font-weight: 700;

  svg {
    width: 10px;
    height: 10px;
  }
`;

// ===== MAP CONTROLS =====
export const MapControls = styled.div`
  position: absolute;
  bottom: 1.5rem;
  right: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: 20;
`;

export const ZoomButtonGroup = styled.div`
  background: ${G.glass.medium};
  backdrop-filter: ${G.blur.lg};
  -webkit-backdrop-filter: ${G.blur.lg};
  border: ${G.border.medium};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: ${G.shadow.dark};
`;

export const ZoomButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #1e293b;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.35);
  }

  &:first-child {
    border-bottom: 1px solid rgba(30, 41, 59, 0.1);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const ResetViewButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${G.glass.medium};
  backdrop-filter: ${G.blur.lg};
  -webkit-backdrop-filter: ${G.blur.lg};
  border: ${G.border.medium};
  color: #1e293b;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.35);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

// ===== LIVE INDICATOR =====
export const LiveIndicator = styled.div`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: ${G.glass.medium};
  backdrop-filter: ${G.blur.lg};
  -webkit-backdrop-filter: ${G.blur.lg};
  border: ${G.border.medium};
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  z-index: 20;
`;

export const LiveDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${C.emerald};
  animation: ${pulseDot} 2s ease-in-out infinite;
`;

export const LiveText = styled.span`
  font-size: 0.625rem;
  font-weight: 500;
  color: #1e293b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const LiveDivider = styled.div`
  width: 1px;
  height: 12px;
  background: rgba(30, 41, 59, 0.2);
`;

export const AvatarStack = styled.div`
  display: flex;

  img, div {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 1px solid #0a0f1e;
    object-fit: cover;
    margin-left: -8px;

    &:first-child {
      margin-left: 0;
    }
  }
`;

export const AvatarCount = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${C.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.5rem;
  font-weight: 700;
  color: ${C.white};
  border: 1px solid #0a0f1e;
  margin-left: -8px;
`;

// ===== MOBILE BOTTOM NAV — glass =====
export const MobileBottomNav = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(10, 16, 36, 0.90);
  backdrop-filter: ${G.blur.lg};
  -webkit-backdrop-filter: ${G.blur.lg};
  @supports not (backdrop-filter: blur(1px)) {
    background: rgba(10, 16, 36, 0.97);
  }
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  padding: 0.5rem 1.5rem calc(0.5rem + env(safe-area-inset-bottom, 0px));
  z-index: ${DesignTokens.zIndex.fixed};
  display: flex;

  @media (min-width: ${props => props.theme.breakpoints.desktop}) {
    display: none;
  }
`;

export const NavItems = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
`;

export const NavItem = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  text-decoration: none;
  color: ${props => props.$active ? C.primary : C.textMuted};
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: ${props => props.$active ? C.primary : C.text};
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

export const NavLabel = styled.span`
  font-size: 0.625rem;
  font-weight: ${props => props.$active ? 600 : 400};
`;

export const FABButton = styled.button`
  position: relative;
  top: -1.5rem;
  width: 56px;
  height: 56px;
  background: ${G.gradient.blueCTA};
  color: ${C.white};
  border-radius: 16px;
  border: none;
  box-shadow: ${G.shadow.blueCTA};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.2s;

  &:hover {
    box-shadow: ${G.shadow.blueCTAHover};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 30px;
    height: 30px;
  }
`;

// ===== SKELETON / LOADING =====
const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

export const SkeletonCard = styled.div`
  background: ${G.glass.light};
  backdrop-filter: ${G.blur.sm};
  -webkit-backdrop-filter: ${G.blur.sm};
  border-radius: 16px;
  border: ${G.border.light};
  overflow: hidden;
`;

export const SkeletonBlock = styled.div`
  background: linear-gradient(
    90deg,
    rgba(255,255,255,0.04) 0%,
    rgba(255,255,255,0.1) 50%,
    rgba(255,255,255,0.04) 100%
  );
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: ${props => props.$round ? '50%' : '6px'};
  width: ${props => props.$width || '100%'};
  height: ${props => props.$height || '16px'};
`;

// ===== EMPTY STATE =====
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem;
  text-align: center;
`;

export const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  color: ${C.textMuted};

  svg {
    width: 28px;
    height: 28px;
  }
`;

export const EmptyTitle = styled.p`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: ${C.text};
`;

export const EmptyText = styled.p`
  font-size: 0.875rem;
  color: ${C.textSecondary};
  margin-bottom: 1.5rem;
`;

export const ResetFiltersButton = styled.button`
  padding: 0.5rem 1.5rem;
  background: ${G.gradient.blueCTA};
  color: ${C.white};
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: ${G.shadow.blueCTA};
  transition: box-shadow 0.2s, opacity 0.2s;

  &:hover {
    opacity: 0.9;
    box-shadow: ${G.shadow.blueCTAHover};
  }
`;

// ===== MOBILE MAP PEEK CARD — expanded glass card =====
export const MapListViewButton = styled.button`
  position: absolute;
  top: 3.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1200;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1.25rem;
  background: rgba(10, 16, 36, 0.88);
  backdrop-filter: ${G.blur.lg};
  -webkit-backdrop-filter: ${G.blur.lg};
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 9999px;
  color: ${C.white};
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  transition: background 0.2s, box-shadow 0.2s;

  svg {
    width: 16px;
    height: 16px;
  }

  &:active {
    transform: translateX(-50%) scale(0.95);
  }

  @media (min-width: ${props => props.theme.breakpoints.desktop}) {
    display: none;
  }
`;

export const MapPeekCard = styled(motion.div)`
  position: absolute;
  bottom: 5rem;
  left: 1rem;
  right: 1rem;
  background: rgba(10, 16, 36, 0.92);
  backdrop-filter: ${G.blur.lg};
  -webkit-backdrop-filter: ${G.blur.lg};
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 25;

  @media (min-width: ${props => props.theme.breakpoints.desktop}) {
    display: none;
  }
`;

export const PeekCardImage = styled.img`
  width: 100%;
  height: 120px;
  object-fit: cover;
  flex-shrink: 0;
`;

export const PeekCardContent = styled.div`
  padding: 1rem 1.125rem 0.875rem;
  min-width: 0;
`;

export const PeekCardTitle = styled.h4`
  font-weight: 700;
  font-size: 1rem;
  line-height: 1.3;
  margin-bottom: 0.375rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #ffffff;
`;

export const PeekCardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 0.625rem;
`;

export const PeekCardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const PeekCardRating = styled.span`
  font-size: 0.75rem;
  color: #facc15;
  font-weight: 700;
`;

export const PeekCardLink = styled.button`
  font-size: 0.8125rem;
  font-weight: 700;
  color: #ffffff;
  background: ${G.gradient.blueCTA};
  border: none;
  padding: 0.4rem 1rem;
  border-radius: 9999px;
  cursor: pointer;
  box-shadow: ${G.shadow.blueCTA};
  transition: box-shadow 0.2s, opacity 0.2s;

  &:hover {
    opacity: 0.9;
    box-shadow: ${G.shadow.blueCTAHover};
  }
`;

// ===== GRADIENT IMAGE PLACEHOLDER =====
export const GradientPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: ${props => props.$gradient || 'linear-gradient(135deg, #1e3a5f 0%, #2d6a4f 100%)'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.5);

  svg {
    width: 32px;
    height: 32px;
  }

  span {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    max-width: 90%;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// ===== DEFAULT AVATAR =====
export const DefaultAvatar = styled.div`
  width: ${props => props.$size || '16px'};
  height: ${props => props.$size || '16px'};
  border-radius: 50%;
  background: ${G.gradient.blueCTA};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${C.white};
  flex-shrink: 0;

  svg {
    width: 60%;
    height: 60%;
  }
`;

// ===== "NEW" BADGE =====
export const NewBadge = styled.span`
  display: inline-block;
  padding: 0.125rem 0.5rem;
  background: rgba(66, 133, 244, 0.15);
  color: ${C.accent};
  font-size: 0.625rem;
  font-weight: 700;
  border-radius: 9999px;
  border: 1px solid rgba(66, 133, 244, 0.3);
`;

// ===== FILTER DROPDOWN — glass =====
export const FilterDropdown = styled(motion.div)`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 180px;
  background: rgba(12, 20, 42, 0.97);
  backdrop-filter: ${G.blur.lg};
  -webkit-backdrop-filter: ${G.blur.lg};
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 14px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.6);
  overflow: hidden;
  z-index: ${DesignTokens.zIndex.dropdown};
`;

export const FilterDropdownItem = styled.button`
  display: block;
  width: 100%;
  padding: 0.625rem 1rem;
  background: ${props => props.$active ? 'rgba(66, 133, 244, 0.18)' : 'transparent'};
  border: none;
  color: ${props => props.$active ? C.accent : C.text};
  font-size: 0.8125rem;
  font-weight: ${props => props.$active ? 600 : 400};
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: ${props => props.$active
      ? 'rgba(66, 133, 244, 0.25)'
      : 'rgba(255, 255, 255, 0.07)'};
  }
`;

// ===== SORT FALLBACK INDICATOR =====
export const SortFallbackBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 9999px;
  background: rgba(250, 204, 21, 0.12);
  color: ${C.yellow};
  font-size: 0.6875rem;
  font-weight: 500;
  white-space: nowrap;

  svg {
    width: 10px;
    height: 10px;
  }
`;

// ===== INFINITE SCROLL SENTINEL =====
export const ScrollSentinel = styled.div`
  height: 1px;
  width: 100%;
`;
